import { useEffect, useMemo, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../components/AuthProvider";
import { featureFlags } from "../../config/featureFlags";

type GuidedStep = {
  id: string;
  title: string;
  stepOrder: number;
  status: string;
  description?: string;
  metadata?: Record<string, any> | null;
};

type GuidedSession = {
  id: string;
  standardCode: string;
  status: string;
  currentStepOrder: number;
  steps: GuidedStep[];
  progress?: { totalSteps: number; completedSteps: number; percent: number };
};
type GuidedEvidenceRequest = {
  id: string;
  stepId?: string | null;
  title: string;
  status: string;
  requiredForAudit: boolean;
  linkedEvidenceId?: string | null;
};
type GuidedWorkflowRun = {
  id: string;
  status: string;
  createdAt?: string;
  workflowDefinition?: { name?: string };
};

type GuidedAssessmentWorkspaceProps = {
  standardCode: string;
  enabledStandards: string[];
};
type EvaluationResult = {
  matchedEffects: Array<{ effectType: string; effectPayload?: Record<string, unknown> | null; sourceType?: string }>;
  hasBlocker: boolean;
};

export default function GuidedAssessmentWorkspace({ standardCode, enabledStandards }: GuidedAssessmentWorkspaceProps) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<GuidedSession | null>(null);
  const [working, setWorking] = useState(false);
  const [evidenceRequests, setEvidenceRequests] = useState<GuidedEvidenceRequest[]>([]);
  const [evidenceTitle, setEvidenceTitle] = useState("");
  const [evaluationQuestionId, setEvaluationQuestionId] = useState("control_coverage");
  const [evaluationAnswer, setEvaluationAnswer] = useState("NO");
  const [evaluationByStep, setEvaluationByStep] = useState<Record<string, EvaluationResult>>({});
  const [aiSessionResult, setAiSessionResult] = useState<any>(null);
  const [workflowRuns, setWorkflowRuns] = useState<GuidedWorkflowRun[]>([]);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [runtimeMessage, setRuntimeMessage] = useState<string | null>(null);

  const selectedStandard = useMemo(() => {
    if (standardCode && standardCode !== "ALL") return standardCode;
    return enabledStandards[0] || "";
  }, [enabledStandards, standardCode]);

  const loadSession = async () => {
    if (!selectedStandard) {
      setSession(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setRuntimeError(null);
    try {
      const listResponse = await fetch(
        `${API_ENDPOINTS.COMPLIANCE_GUIDED_SESSIONS}?standardCode=${encodeURIComponent(selectedStandard)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const listJson = await listResponse.json().catch(() => ({}));
      const sessions = Array.isArray(listJson?.data) ? listJson.data : [];
      const latest = sessions.find((item: any) => {
        const status = String(item?.status || "").toUpperCase();
        return status === "ACTIVE" || status === "IN_PROGRESS";
      }) || sessions[0] || null;
      if (!latest?.id) {
        setSession(null);
        setRuntimeMessage("No guided session exists yet for this standard.");
        return;
      }
      const sessionResponse = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSION(latest.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sessionJson = await sessionResponse.json().catch(() => ({}));
      const sessionData = sessionJson?.data || null;
      setSession(sessionData);
      if (Array.isArray(sessionData?.steps)) {
        const persistedEvaluations = sessionData.steps.reduce((acc: Record<string, EvaluationResult>, step: any) => {
          const runtimeEvaluation = step?.metadata?.runtimeEvaluation;
          if (!runtimeEvaluation) return acc;
          acc[step.id] = {
            matchedEffects: Array.isArray(runtimeEvaluation.matchedEffects) ? runtimeEvaluation.matchedEffects : [],
            hasBlocker: Boolean(runtimeEvaluation.hasBlocker),
          };
          return acc;
        }, {});
        setEvaluationByStep(persistedEvaluations);
      }
      if (sessionData?.id) {
        const evidenceResponse = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSION_EVIDENCE_REQUESTS(sessionData.id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const evidenceJson = await evidenceResponse.json().catch(() => ({}));
        setEvidenceRequests(Array.isArray(evidenceJson?.data) ? evidenceJson.data : []);
        const runsResponse = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_WORKFLOW_RUNS(sessionData.id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const runsJson = await runsResponse.json().catch(() => ({}));
        setWorkflowRuns(Array.isArray(runsJson?.data) ? runsJson.data : []);
        setRuntimeMessage(
          String(sessionData?.status || "").toUpperCase() === "COMPLETED"
            ? "Latest session is completed. You can start a new session when needed."
            : null,
        );
      }
    } catch (error: any) {
      setRuntimeError(error?.message || "Failed to load guided session runtime.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSession();
  }, [selectedStandard, token]);

  const startOrResume = async () => {
    if (!selectedStandard) return;
    setWorking(true);
    setRuntimeError(null);
    try {
      const response = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSIONS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ standardCode: selectedStandard }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || `Failed to start guided session (${response.status})`);
      }
      const created = json?.data;
      if (created?.id) {
        const details = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSION(created.id), {
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => res.json().catch(() => ({})));
        setSession(details?.data || created);
        const evidenceResponse = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSION_EVIDENCE_REQUESTS(created.id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const evidenceJson = await evidenceResponse.json().catch(() => ({}));
        setEvidenceRequests(Array.isArray(evidenceJson?.data) ? evidenceJson.data : []);
        const runsResponse = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_WORKFLOW_RUNS(created.id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const runsJson = await runsResponse.json().catch(() => ({}));
        setWorkflowRuns(Array.isArray(runsJson?.data) ? runsJson.data : []);
      }
    } catch (error: any) {
      setRuntimeError(error?.message || "Failed to start or resume guided session.");
    } finally {
      setWorking(false);
    }
  };

  const createEvidenceRequest = async (stepId?: string) => {
    if (!session?.id || !evidenceTitle.trim()) return;
    setWorking(true);
    setRuntimeError(null);
    try {
      const response = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSION_EVIDENCE_REQUESTS(session.id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          stepId: stepId || null,
          title: evidenceTitle.trim(),
          requiredForAudit: true,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || `Failed to create evidence request (${response.status})`);
      }
      const created = json?.data;
      if (created?.id) {
        setEvidenceRequests((prev) => [created, ...prev.filter((item) => item.id !== created.id)]);
      } else {
        await loadSession();
      }
      setEvidenceTitle("");
      setRuntimeMessage("Evidence request created successfully.");
    } catch (error: any) {
      setRuntimeError(error?.message || "Failed to create evidence request.");
    } finally {
      setWorking(false);
    }
  };

  const markStepCompleted = async (stepId: string) => {
    if (!session?.id) return;
    setWorking(true);
    setRuntimeError(null);
    try {
      const response = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_SESSION_STEP(session.id, stepId), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: "COMPLETED" }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || `Failed to update step (${response.status})`);
      }
      const payload = json?.data || {};
      if (payload?.session) {
        setSession({
          ...payload.session,
          progress: payload.progress || payload.session.progress,
        });
        setRuntimeMessage("Step completed and progress updated.");
      } else {
        await loadSession();
      }
    } catch (error: any) {
      setRuntimeError(error?.message || "Failed to mark step as completed.");
    } finally {
      setWorking(false);
    }
  };

  const evaluateStep = async (stepId: string) => {
    if (!session?.id) return;
    setWorking(true);
    setRuntimeError(null);
    try {
      const response = await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_STEP_EVALUATE(session.id, stepId), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          questionId: evaluationQuestionId,
          answerValue: evaluationAnswer,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json?.message || `Failed to evaluate step (${response.status})`);
      }
      const result = (json?.data || { matchedEffects: [], hasBlocker: false }) as EvaluationResult;
      setEvaluationByStep((prev) => ({ ...prev, [stepId]: result }));
      setRuntimeMessage(result.matchedEffects.length ? "Rule evaluation completed." : "No matching rules for current input.");
    } catch (error: any) {
      setRuntimeError(error?.message || "Failed to evaluate dynamic rules.");
    } finally {
      setWorking(false);
    }
  };

  if (!selectedStandard) {
    return (
      <div className="rounded-lg border border-amber-200 bg-white p-4 text-sm text-amber-800">
        Select at least one enabled standard to start guided assessments.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-textMain">Guided Assessment Journey</h3>
            <p className="text-sm text-gray-600 mt-1">
              Standard: <span className="font-medium">{selectedStandard}</span>
            </p>
          </div>
          <button
            type="button"
            className="px-3 py-2 text-sm rounded-md bg-sidebarActive text-white disabled:opacity-50"
            onClick={() => void startOrResume()}
            disabled={working || loading}
          >
            {session ? "Resume session" : "Start guided session"}
          </button>
        </div>
      </div>
      {runtimeError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{runtimeError}</div>
      ) : null}
      {runtimeMessage ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{runtimeMessage}</div>
      ) : null}

      {loading ? <div className="text-sm text-gray-600">Loading guided session...</div> : null}

      {!loading && !session ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
          No guided session exists yet for this standard.
        </div>
      ) : null}

      {session ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <div className="rounded border border-gray-200 p-3">
            <div className="text-sm font-medium text-textMain mb-2">AI session actions (manual, with confirmation by user)</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                disabled={working || !featureFlags.AI_AUDIT_ASSISTANT_V1}
                onClick={async () => {
                  setWorking(true);
                  try {
                    const res = await fetch(API_ENDPOINTS.AI_GUIDED_SESSION_GAP_ANALYSIS(session.id), {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const json = await res.json().catch(() => ({}));
                    setAiSessionResult({ action: "Gap analysis", ok: res.ok, payload: json?.data || json });
                  } finally {
                    setWorking(false);
                  }
                }}
              >
                Run AI gap analysis
              </button>
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                disabled={working || !featureFlags.AI_AUDIT_ASSISTANT_V1}
                onClick={async () => {
                  setWorking(true);
                  try {
                    const res = await fetch(API_ENDPOINTS.AI_GUIDED_SESSION_REMEDIATION_PLAN(session.id), {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const json = await res.json().catch(() => ({}));
                    setAiSessionResult({ action: "Remediation plan", ok: res.ok, payload: json?.data || json });
                  } finally {
                    setWorking(false);
                  }
                }}
              >
                Run AI remediation plan
              </button>
            </div>
            {aiSessionResult ? (
              <div className="mt-2 text-xs text-gray-700">
                <div>
                  Result: {aiSessionResult.action} · {aiSessionResult.ok ? "success" : "failed"}
                </div>
                <div className="text-gray-600">AI outputs are advisory and require human confirmation before application.</div>
              </div>
            ) : null}
            {!featureFlags.AI_AUDIT_ASSISTANT_V1 ? (
              <div className="mt-2 text-xs text-amber-700">
                AI session actions are disabled by feature flags in this environment.
              </div>
            ) : null}
          </div>
          <div className="rounded border border-gray-200 p-3">
            <div className="text-sm font-medium text-textMain mb-2">Workflow orchestration</div>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                disabled={working}
                onClick={async () => {
                  setWorking(true);
                  try {
                    await fetch(API_ENDPOINTS.COMPLIANCE_GUIDED_WORKFLOW_RUNS(session.id), {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                      body: JSON.stringify({ templateType: "evidence_collection", metadata: { source: "guided-workspace" } }),
                    });
                    await loadSession();
                  } finally {
                    setWorking(false);
                  }
                }}
              >
                Create evidence workflow run
              </button>
            </div>
            <div className="space-y-1">
              {workflowRuns.map((run) => (
                <div key={run.id} className="text-xs text-gray-700">
                  {run.workflowDefinition?.name || "Workflow"} · {run.status} · {run.createdAt ? new Date(run.createdAt).toLocaleString() : ""}
                </div>
              ))}
              {!workflowRuns.length ? <div className="text-xs text-gray-500">No workflow runs for this guided session.</div> : null}
            </div>
          </div>
          <div className="text-sm text-gray-700">
            Progress: {session.progress?.completedSteps || 0}/{session.progress?.totalSteps || session.steps?.length || 0} (
            {session.progress?.percent ?? 0}%)
          </div>
          <div className="space-y-2">
            {session.steps?.map((step) => (
              <div key={step.id} className="rounded-md border border-gray-200 p-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-textMain">
                    {step.stepOrder}. {step.title}
                  </div>
                  <div className="text-xs text-gray-600">{step.status}</div>
                </div>
                {step.status !== "COMPLETED" ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => void markStepCompleted(step.id)}
                      disabled={working}
                    >
                      Mark complete
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => void createEvidenceRequest(step.id)}
                      disabled={working || !evidenceTitle.trim()}
                    >
                      Request evidence
                    </button>
                    <button
                      type="button"
                      className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => void evaluateStep(step.id)}
                      disabled={working}
                    >
                      Evaluate
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <div className="rounded-md border border-dashed border-gray-300 p-3">
            <div className="text-xs text-gray-600 mb-2">Dynamic question input</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                value={evaluationQuestionId}
                onChange={(event) => setEvaluationQuestionId(event.target.value)}
                placeholder="questionId (e.g. control_coverage)"
              />
              <input
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                value={evaluationAnswer}
                onChange={(event) => setEvaluationAnswer(event.target.value)}
                placeholder="answer value (e.g. NO)"
              />
            </div>
          </div>
          {session.steps.map((step) => {
            const evalResult = evaluationByStep[step.id];
            if (!evalResult) return null;
            return (
              <div key={`eval-${step.id}`} className="rounded border border-gray-200 p-2 text-xs">
                <div className="font-medium text-textMain">
                  Dynamic result for step {step.stepOrder}: {evalResult.hasBlocker ? "Blocker detected" : "No blocker"}
                </div>
                {!evalResult.matchedEffects.length ? (
                  <div className="text-gray-600">No follow-up effects triggered.</div>
                ) : (
                  <div className="space-y-1 mt-1">
                    {evalResult.matchedEffects.map((effect, idx) => (
                      <div key={`${step.id}-effect-${idx}`} className="text-gray-700">
                        {effect.effectType}: {JSON.stringify(effect.effectPayload || {})}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="rounded-md border border-dashed border-gray-300 p-3">
            <div className="text-xs text-gray-600 mb-2">Evidence request title</div>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                value={evidenceTitle}
                onChange={(event) => setEvidenceTitle(event.target.value)}
                placeholder="e.g. Upload policy approval evidence"
              />
              <button
                type="button"
                className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => void createEvidenceRequest()}
                disabled={working || !evidenceTitle.trim()}
              >
                Create
              </button>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-textMain mb-2">Evidence requests</div>
            <div className="space-y-2">
              {evidenceRequests.map((item) => (
                <div key={item.id} className="rounded border border-gray-200 p-2 text-xs text-gray-700">
                  <div className="font-medium text-textMain">{item.title}</div>
                  <div>
                    Status: {item.status} · Required for audit: {item.requiredForAudit ? "Yes" : "No"} · Linked:{" "}
                    {item.linkedEvidenceId ? "Yes" : "No"}
                  </div>
                </div>
              ))}
              {!evidenceRequests.length ? <div className="text-xs text-gray-500">No evidence requests yet.</div> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

